import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../../../helpers/apiRequester'
import { InputFieldAddressLookup } from '../../../reusable/inputFieldAddressLookup'
import { InputFieldText } from '../../../reusable/inputFieldText'
import styles from './styles.module.scss'
import Modal from '../../../reusable/modal'
import { ClientCreatedResultModalContent } from '../../../modalContent/createClientResult'

export const CreateClientView = () => {
    const navigate = useNavigate()
    const [addressSelected, setAddressSelected] = useState(false)

    const [address_line_one, setAddressLineOne] = useState('')
    const [address_line_two, setAddressLineTwo] = useState('')
    const [address_line_three, setAddressLineThree] = useState('')
    const [city, setCity] = useState('')
    const [postcode, setPostcode] = useState('')
    const [createResult, setCreateResult] = useState(false)
    const [esignUrl, setEsignUrl] = useState('')

    const [clientData, setClientData] = useState({
        firstname: '',
        lastname: '',
        title: '',
        dob: '',
        email: '',
        telephone: '',
        address_line_one: '',
        address_line_two: '',
        address_line_three: '',
        city: '',
        postcode: '',
        origin: '',
    })

    const [errors, setErrors] = useState({
        email: '',
        telephone: '',
        dob: '',
    })

    // Calculate the maximum date (18 years ago from today)
    const getMaxDate = () => {
        const today = new Date()
        const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate())
        return maxDate.toISOString().split('T')[0]
    }

    // Validate age (must be 18 or older)
    const validateAge = (dob: string) => {
        if (!dob) return true // Allow empty DOB

        const birthDate = new Date(dob)
        const today = new Date()
        const age = today.getFullYear() - birthDate.getFullYear()
        const monthDiff = today.getMonth() - birthDate.getMonth()

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            return age - 1 >= 18
        }

        return age >= 18
    }

    useEffect(() => {
        setClientData((prevData) => ({
            ...prevData,
            origin: window.location.href,
        }))
    }, [])

    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setClientData((prevData) => ({ ...prevData, [name]: value }))

        // Validate date of birth
        if (name === 'dob') {
            if (value && !validateAge(value)) {
                setErrors((prevErrors) => ({ ...prevErrors, dob: 'Client must be 18 years or older' }))
            } else {
                setErrors((prevErrors) => ({ ...prevErrors, dob: '' }))
            }
        }
    }

    const saveValue = ({ field, value }: { field: string; value: string }) => {
        setClientData((prevData) => ({
            ...prevData,
            [field]: value,
        }))

        if (field === 'email' && value === '') {
            setErrors((prevErrors) => ({ ...prevErrors, email: 'Invalid email' }))
            return { success: false, error: 'Invalid email' }
        }

        if (field === 'telephone' && value === '') {
            setErrors((prevErrors) => ({
                ...prevErrors,
                telephone: 'Invalid phone number',
            }))
            return { success: false, error: 'Invalid phone number' }
        }

        setErrors((prevErrors) => ({ ...prevErrors, [field]: '' }))
        return { success: true, error: '' }
    }

    const handleAddressSelection = ({ field, value }: { field: string; value: any }) => {
        if (field === 'address') {
            parseAndSaveAddress(value)
            return { success: true, error: '' }
        }
        setClientData((prevData) => ({
            ...prevData,
            raw_address: value,
        }))
        return { success: true, error: '' }
    }

    const parseAndSaveAddress = (addressString: any) => {
        const addressArr = addressString.split(',')

        let addressLineOne = addressArr[0]
        let addressLineTwo = addressArr.length > 3 ? addressArr[1] : ''
        let addressLineThree = addressArr.length > 4 ? addressArr[addressArr.length - 3] : ''
        let c = addressArr[addressArr.length - 2]
        let p = addressArr[addressArr.length - 1]

        setAddressLineOne(addressLineOne)
        setAddressLineTwo(addressLineTwo)
        setAddressLineThree(addressLineThree)
        setCity(c)
        setPostcode(p)

        let tempData = {
            ...clientData,
        }

        tempData.address_line_one = addressLineOne
        tempData.address_line_two = addressLineTwo
        tempData.address_line_three = addressLineThree
        tempData.city = c
        tempData.postcode = p

        setClientData(tempData)

        // console.log('clientData', clientData)
        setAddressSelected(true)
    }

    const closeModal = () => {
        setCreateResult(false)
        setEsignUrl('')
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        console.log('clientData', clientData)

        if (!clientData.address_line_one || !clientData.postcode) {
            Report.failure('Error', 'Address is required.', 'Okay')
            return
        }

        if (errors.email || errors.telephone || errors.dob) {
            Report.failure('Error', 'Please fix the validation errors before submitting.', 'Okay')
            return
        }

        Loading.circle('Saving...')

        const submissionData = {
            ...clientData,
            leadSource: clientData.origin,
        }

        console.log('Submitting Client Data:', submissionData)

        try {
            const apiResult = await api({
                url: 'pcp-sentinel/create-client',
                method: 'POST',
                data: submissionData,
            })

            Loading.remove()
            if (!apiResult.data.success) {
                return Report.failure('Error', 'An error occurred.', 'Okay')
            }

            setCreateResult(true)
            setEsignUrl(apiResult?.data?.claimData?.signature_link)

            setClientData({
                firstname: '',
                lastname: '',
                title: '',
                dob: '',
                email: '',
                telephone: '',
                address_line_one: '',
                address_line_two: '',
                address_line_three: '',
                city: '',
                postcode: '',
                origin: '',
            })

            setAddressSelected(false)
            setAddressLineOne('')
            setAddressLineTwo('')
            setAddressLineThree('')
            setCity('')
            setPostcode('')
        } catch (error) {
            Loading.remove()
            Report.failure('Error', 'An error occurred while creating the client.', 'Okay')
        }
    }

    return (
        <div className={styles.clientView}>
            <h3>
                <span>Create New Client</span>
            </h3>
            <form onSubmit={handleSubmit}>
                <fieldset>
                    <label>First Name</label>
                    <input
                        className={styles.textInput}
                        name="firstname"
                        value={clientData.firstname}
                        onChange={onChange}
                        type="text"
                        placeholder="Enter first name"
                        required
                    />
                </fieldset>
                <fieldset>
                    <label>Last Name</label>
                    <input
                        className={styles.textInput}
                        name="lastname"
                        value={clientData.lastname}
                        onChange={onChange}
                        type="text"
                        placeholder="Enter last name"
                        required
                    />
                </fieldset>
                <fieldset>
                    <label>Title</label>
                    <select
                        className={styles.textInput}
                        name="title"
                        value={clientData.title}
                        onChange={onChange}
                        required
                    >
                        <option value="">Select title</option>
                        <option value="Mr">Mr</option>
                        <option value="Mrs">Mrs</option>
                        <option value="Ms">Ms</option>
                        <option value="Miss">Miss</option>
                        <option value="Dr">Dr</option>
                    </select>
                </fieldset>

                <fieldset>
                    <label>Date of Birth</label>
                    <input
                        className={`${styles.textInput} ${errors.dob ? styles.error : ''}`}
                        name="dob"
                        value={clientData.dob}
                        onChange={onChange}
                        type="date"
                        max={getMaxDate()}
                        placeholder="Select date of birth"
                    />
                    {errors.dob && <span className={styles.errorMessage}>{errors.dob}</span>}
                </fieldset>

                <fieldset>
                    <label>Email Address</label>
                    <InputFieldText
                        fieldName="email"
                        fieldType="email"
                        saveValue={saveValue}
                        populateValue={clientData.email}
                        required
                        showErrors
                    />
                </fieldset>

                <fieldset>
                    <label>Telephone Number</label>
                    <InputFieldText
                        fieldName="telephone"
                        fieldType="tel"
                        saveValue={saveValue}
                        populateValue={clientData.telephone}
                        required
                        showErrors
                    />
                </fieldset>

                <div className={styles.formDiv}>
                    <h4>Client Address</h4>

                    <fieldset>
                        <label>Search Address</label>
                        <InputFieldAddressLookup
                            fieldName="searchAddress"
                            saveValue={handleAddressSelection}
                            enhanced={true}
                        />
                    </fieldset>
                    {addressSelected && (
                        <>
                            {address_line_one && (
                                <fieldset>
                                    <label>Street Address</label>
                                    <input
                                        className={styles.textInput}
                                        name="address_line_one"
                                        value={address_line_one}
                                        onChange={onChange}
                                        type="text"
                                        placeholder="Street address"
                                        required
                                    />
                                </fieldset>
                            )}

                            {address_line_two && (
                                <fieldset>
                                    <label>Street Address Line 2</label>
                                    <input
                                        className={styles.textInput}
                                        name="address_line_two"
                                        value={address_line_two}
                                        onChange={onChange}
                                        type="text"
                                        placeholder="Additional address line"
                                    />
                                </fieldset>
                            )}

                            {address_line_two && (
                                <fieldset>
                                    <label>Town/City</label>
                                    <input
                                        className={styles.textInput}
                                        name="city"
                                        value={city}
                                        onChange={onChange}
                                        type="text"
                                        placeholder="Town or city"
                                        required
                                    />
                                </fieldset>
                            )}

                            {city && (
                                <fieldset>
                                    <label>City</label>
                                    <input
                                        className={styles.textInput}
                                        name="city"
                                        value={city}
                                        onChange={onChange}
                                        type="text"
                                        placeholder="City"
                                        required
                                    />
                                </fieldset>
                            )}

                            <fieldset>
                                <label>Postcode</label>
                                <input
                                    className={styles.textInput}
                                    name="postcode"
                                    value={postcode}
                                    onChange={onChange}
                                    type="text"
                                    placeholder="Postcode"
                                    required
                                />
                            </fieldset>
                        </>
                    )}
                </div>

                <button type="submit">
                    <span>Create Client</span>
                </button>
            </form>
            <Modal modalState={createResult} previewModalAction={closeModal}>
                <ClientCreatedResultModalContent success={createResult} url={esignUrl} />
            </Modal>
        </div>
    )
}
